// Client HTTP central de cc-jaw.
// - Prefixe l'URL de base de l'API (meme source que getEntraConfig().apiBaseUrl).
// - Injecte le Bearer token si fourni.
// - INTERCEPTEUR 401 : sur reponse 401 d'un appel authentifie, declenche le
//   handler global (enregistre par AuthContext) -> session videe + redirection
//   vers l'authentification. C'est le filet de securite cote frontend ;
//   la vraie protection reste le 401 du backend.

import { getEntraConfig } from '../auth/entraConfig';
import { getSessionId, nextSeq, parentSeq } from '../insights/session';
import { causeGarantie, callStart, track } from '../insights/beacon';

export class ApiError extends Error {
  constructor(public status: number, public body?: unknown) {
    super(`API ${status}`);
  }
}

/** Erreur réseau / service injoignable / timeout (status 0). */
export class NetworkError extends Error {
  constructor(public reason: 'timeout' | 'network', public cause?: unknown) {
    super(`Network ${reason}`);
  }
}

/** Délai max par défaut d'une requête (ms) avant abandon -> NetworkError('timeout').
 *  30 s pour absorber un COLD START de l'ACA (scale-to-zero) sans timeout. */
export const DEFAULT_TIMEOUT_MS = 30000;

/** fetch avec timeout (AbortController) + retry sur cold start.
 *  Lève NetworkError sur timeout/coupure. Le retry n'est fait que pour les
 *  méthodes IDEMPOTENTES (GET/HEAD) : jamais sur POST/PUT (ex. buy) pour éviter
 *  un double effet de bord si la requête a en fait atteint le serveur. */
/** Ajoute les en-têtes de CORRÉLATION s'ils manquent, et seulement vers NOTRE API.
 *
 * POURQUOI ICI (CCT-252). Ces en-têtes n'étaient posés que par `apiFetch`. Or plusieurs
 * chemins appellent `fetchWithTimeout` DIRECTEMENT — tout `MissionsContext` (marketplace,
 * missions, panier), c'est-à-dire l'essentiel de ce que fait l'écran d'accueil. Ces
 * requêtes arrivaient donc SANS identifiant de session : le middleware serveur en
 * synthétisait un (`srv-…`) et la navigation de l'utilisateur n'apparaissait dans AUCUNE
 * session — alors que les spans SQL enfants, eux, portaient le bon id. Une session
 * dissociée d'elle-même, et un écran Sessions vide pour un utilisateur qui venait de
 * naviguer.
 *
 * Le poser au point de passage OBLIGATOIRE plutôt que dans chaque appelant : `apiFetch`
 * passe lui aussi par ici, et un nouveau chemin d'appel n'aura rien à penser.
 *
 * DEUX GARDES :
 *  - **seulement vers notre API** : ajouter un en-tête personnalisé à une requête d'une
 *    autre origine déclenche un préflight CORS que ce tiers refusera — on casserait
 *    l'appel pour de la télémétrie ;
 *  - **jamais d'écrasement** : `apiFetch` a déjà consommé un numéro de séquence pour cette
 *    requête. Le remplacer ici ferait diverger le rang envoyé de celui qu'il croit avoir
 *    envoyé, et brûlerait un numéro à chaque appel.
 */
function withCorrelation(input: string, init: RequestInit): RequestInit {
  try {
    const base = getEntraConfig().apiBaseUrl.replace(/\/$/, '');
    if (!base || !input.startsWith(base)) return init;
    const headers = new Headers(init.headers as HeadersInit | undefined);
    if (!headers.has('X-CC-Session')) headers.set('X-CC-Session', getSessionId());
    // APPLICATION D'ORIGINE (CCT-253) : rien ne la portait, et l'écran ne pouvait donc pas
    // distinguer une session du joueur d'une session du back-office. La DEVINER (par
    // l'origine, par les chemins appelés) donnerait une pastille parfois fausse — pire
    // qu'absente sur un écran de diagnostic.
    if (!headers.has('X-CC-App')) headers.set('X-CC-App', 'app');
    if (!headers.has('X-CC-Session-Sequence')) headers.set('X-CC-Session-Sequence', String(nextSeq()));
    // PARENT (CCT-253) : l'action cliente qui a causé cet appel. `seq` dit QUAND il est
    // parti, `parent` dit POURQUOI — et c'est le second qui permet de rattacher un appel
    // asynchrone au clic qui l'a déclenché, même s'il retombe après un autre clic.
    if (!headers.has('X-CC-Session-Caller-Sequence')) {
      // `sansCause` est DÉCLARÉ par l'appelant, il ne se déduit plus de `background`
      // (CCT-259) : ce dernier ne concerne que l'indicateur de chargement global (CCT-177).
      // Les confondre marquait « sans cause » des appels qui en ont une — le rafraîchissement
      // des notifications, devenu manuel, en était le dernier exemple.
      const background = (init as { withoutCause?: boolean }).withoutCause === true;
      const p = parentSeq(background);
      // TOUJOURS RÉPONDRE, même « je n'en ai pas » (CCT-259).
      //
      // Un en-tête omis ne dit rien : appel de background légitime, ou cause PERDUE ? Les deux se
      // comptaient pareil à l'audit, et « zéro écart » n'était atteignable qu'en fermant les
      // yeux sur les vrais défauts. Pire, il devenait impossible de distinguer une session
      // enregistrée par le code ACTUEL d'une session antérieure au marqueur — donc de savoir
      // ce que le contrat peut légitimement exiger.
      //
      // Trois réponses possibles, toutes explicites :
      //   <n>         l'action cliente qui a causé cet appel ;
      //   `none`      appel de FOND (rafraîchissement périodique, minuterie) : aucune cause ;
      //   `bootstrap` aucune action cliente n'a encore eu lieu (démarrage, restauration).
      //
      // ⚠️ `none` ET `bootstrap` SONT SUPPRIMÉS (CCT-262). Le modèle exige que TOUT appel ait
      // une action cliente pour parent ; s'en dispenser par déclaration rendait « zéro écart »
      // atteignable sans rien corriger. Ces appels ont bien une cause — elle n'était
      // représentée par aucun évènement. `causeGarantie` émet celui qui manque (`boot` pour un
      // appel parti avant toute action, `wake` pour le réveil poussé par le service worker) et
      // l'appel le désigne. Si l'émission elle-même échoue, on retombe sur le marqueur textuel
      // plutôt que d'omettre l'en-tête : un en-tête absent ne dit rien.
      const cause = p !== null ? p : causeGarantie(background ? 'wake' : 'boot');
      headers.set('X-CC-Session-Caller-Sequence',
        cause !== null ? String(cause) : (background ? 'none' : 'bootstrap'));
    }
    return { ...init, headers };
  } catch {
    return init;   // l'observabilité ne fait jamais échouer un appel métier
  }
}

export async function fetchWithTimeout(
  input: string,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<Response> {
  init = withCorrelation(input, init);
  const method = (init.method || 'GET').toUpperCase();
  const maxRetries = method === 'GET' || method === 'HEAD' ? 1 : 0;
  let lastErr: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(input, { ...init, signal: controller.signal });
    } catch (e: any) {
      lastErr = new NetworkError(controller.signal.aborted ? 'timeout' : 'network', e);
      if (attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, 800)); // laisse le conteneur démarrer
        continue;
      }
    } finally {
      clearTimeout(timer);
    }
  }
  throw lastErr;
}

import { traceAuth } from '../auth/authTrace';

let onUnauthorized: () => void = () => {};
/** Enregistre le handler appele sur 401 (typiquement par AuthContext). */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

// Tente de RENOUVELER la session (pose par AuthContext). Un 401 ne doit pas signifier
// « dehors » tant qu'un refresh_token est utilisable : le renouvellement proactif (toutes les
// 60 s) peut avoir ete manque — onglet en arriere-plan throttle, machine en veille, hoquet
// reseau. On ne deconnecte qu'apres l'echec du refresh.
let tryRefresh: () => Promise<string | null> = async () => null;
export function setRefreshHandler(fn: () => Promise<string | null>): void {
  tryRefresh = fn;
}

// Jeton COURANT en stock (pose par AuthContext) : sert a distinguer « le refresh a echoue »
// de « un autre appel concurrent l'a deja fait ». Sans cette distinction, N appels simultanes
// produisaient N-1 fausses deconnexions.
let currentToken: () => string | null = () => null;
export function setCurrentTokenReader(fn: () => string | null): void {
  currentToken = fn;
}
/** Declenche manuellement l'intercepteur 401 (pour les appels fetch directs). */
export function notifyUnauthorized(): void {
  onUnauthorized();
}

// --- Indicateur de chargement GLOBAL -------------------------------------
// Registre des requetes FOREGROUND en vol (jeton -> horodatage de debut). L'overlay
// global (GlobalLoadingIndicator) s'affiche tant qu'au moins un jeton est active.
//
// Robustesse (CCT-177) :
//  - Les requetes de FOND (poll notifications via apiFetch({ background:true })) ne sont PAS
//    enregistrees : un poll lent/qui se chevauche ne doit jamais epingler le spinner.
//  - WATCHDOG : un jeton laisse ouvert au-dela de STUCK_MS est purge automatiquement (et l'etat
//    re-emis) — le spinner se demasque tout seul meme si un appelant oublie d'appairer endRequest.
type LoadingListener = (active: boolean) => void;
type RequestToken = number;
const _active = new Map<RequestToken, number>(); // token -> startedAt (ms)
let _nextToken = 1;
const _loadingListeners = new Set<LoadingListener>();

// Plafond au-dela duquel un jeton est considere « bloque » : au-dessus du pire cas reseau
// (timeout + retry GET + backoff, cf. fetchWithTimeout), avec une marge.
const STUCK_MS = DEFAULT_TIMEOUT_MS * 2 + 5000; // ~65 s

const _now = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

function _sweep(): void {
  if (_active.size === 0) return;
  const cutoff = _now() - STUCK_MS;
  for (const [tok, startedAt] of _active) {
    if (startedAt < cutoff) _active.delete(tok);
  }
}

// Reveil differe : garantit une purge + re-emission meme SANS nouvelle activite reseau (sinon un
// jeton bloque resterait affiche jusqu'a la prochaine requete). Un seul timer a la fois.
let _sweepTimer: ReturnType<typeof setTimeout> | null = null;
function _scheduleSweep(): void {
  if (_sweepTimer !== null || _active.size === 0) return;
  _sweepTimer = setTimeout(() => {
    _sweepTimer = null;
    _emitLoading();       // purge (via _sweep) + re-emission
    _scheduleSweep();     // reprogramme s'il reste des jetons actifs
  }, STUCK_MS + 1000);
}

function _emitLoading(): void {
  _sweep();
  const active = _active.size > 0;
  _loadingListeners.forEach((l) => l(active));
}

/** S'abonne a l'etat de chargement global. Retourne une fonction de desabonnement. */
export function subscribeLoading(listener: LoadingListener): () => void {
  _loadingListeners.add(listener);
  _sweep();
  listener(_active.size > 0);
  return () => {
    _loadingListeners.delete(listener);
  };
}

/** Signale le debut d'une requete foreground. Retourne un JETON a rendre a endRequest
 *  (dans un finally). Les requetes de background n'appellent PAS cette fonction. */
export function beginRequest(): RequestToken {
  const token = _nextToken++;
  _active.set(token, _now());
  _scheduleSweep();
  _emitLoading();
  return token;
}

/** Signale la fin d'une requete foreground (jeton rendu par beginRequest). */
export function endRequest(token?: RequestToken): void {
  if (token !== undefined) _active.delete(token);
  _emitLoading();
}

/** Enrobe une promesse pour qu'elle alimente l'indicateur global. */
export async function trackRequest<T>(p: Promise<T>): Promise<T> {
  const token = beginRequest();
  try {
    return await p;
  } finally {
    endRequest(token);
  }
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  token?: string | null;
  /** true => appel authentifie : un 401 declenche l'intercepteur. */
  auth?: boolean;
  /** Délai max avant abandon (ms). Défaut: DEFAULT_TIMEOUT_MS. */
  timeoutMs?: number;
  /** true => requete de FOND (poll, refresh silencieux) : n'alimente PAS l'indicateur de
   *  chargement global (CCT-177). La requete part quand meme ; elle n'est juste pas comptee. */
  background?: boolean;
  /** true => cet appel n'a AUCUNE action cliente à l'origine (CCT-259) : réveil poussé par le
   *  service worker, minuterie. Distinct de `background`, qui ne concerne que l'affichage du
   *  chargement — les confondre marquait « sans cause » des appels qui en ont une. */
  withoutCause?: boolean;
}

export async function apiFetch<T = any>(path: string, opts: ApiOptions = {}, retried = false): Promise<T> {
  const base = getEntraConfig().apiBaseUrl.replace(/\/$/, '');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.token) headers['Authorization'] = `Bearer ${opts.token}`;
  // App Insight interne (CCT-193) : corrélation API + SQL par session. Un seul endroit.
  try { headers['X-CC-Session'] = getSessionId(); } catch { /* best-effort */ }
  // CCT-240 — ORDRE dans la session. Le couple (session, séquence) donne un ordre TOTAL
  // et déterministe à la timeline, là où l'horodatage échoue : navigateur et serveur
  // n'ont pas la même horloge, et l'horodatage d'un span est son DÉBUT, pas sa fin.
  // Clé d'OBSERVABILITÉ uniquement — le serveur la valide et l'ignore si elle est absurde.
  // APPEL CLIENT INSTRUMENTÉ (CCT-274) : rang pris à l'ENVOI — c'est lui qu'on annonce et que
  // le span serveur désignera comme cause — évènement empilé à la RÉCEPTION, avec la pile
  // d'appel et la latence perçue. La différence entre cette latence et le temps de traitement
  // serveur EST le coût réseau, invisible jusqu'ici.
  let _call: { seq: number; seqApi: number; fin: (s: number | null) => void } | null = null;
  try {
    _call = callStart(path, (opts.method || 'GET').toUpperCase());
    headers['X-CC-Session-Sequence'] = String(_call.seqApi);
    // La CAUSE du traitement serveur est l'appel client qui l'a emis, pas l'action utilisateur
    // trois niveaux plus haut : `client action -> appel client -> api -> sql`.
    headers['X-CC-Session-Caller-Sequence'] = String(_call.seq);
  } catch {
    try { headers['X-CC-Session-Sequence'] = String(nextSeq()); } catch { /* best-effort */ }
  }

  // Requete de FOND : ne pas alimenter l'indicateur global (CCT-177).
  const loadToken = opts.background ? undefined : beginRequest();
  try {
    const res = await fetchWithTimeout(
      `${base}${path}`,
      {
        method: opts.method || 'GET',
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        // Transmis à `withCorrelation` (propriété ignorée par `fetch`) : une requête de FOND
        // n'a pas de cause utilisateur et ne doit donc PAS annoncer de parent. Sans ce
        // passage, le polling des notifications se serait rattaché au dernier clic et aurait
        // prêté à l'utilisateur une intention qu'il n'a pas eue.
        background: opts.background === true,
        withoutCause: opts.withoutCause === true,
      } as RequestInit,
      opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    );

    // Clos DES que le statut est connu, avant tout aiguillage : les chemins d'erreur sont
    // precisement ceux qu'on veut voir, et un `finally` les manquerait tous puisque plusieurs
    // d'entre eux relancent l'appel au lieu de retomber ici.
    try { _call?.fin(res.status); _call = null; } catch { /* best-effort */ }

    if (res.status === 401) {
      traceAuth('http-401', opts.token ?? null, path);
      // 401 sur un appel authentifie : on tente D'ABORD un renouvellement + UN rejeu. Le
      // renouvellement proactif peut avoir ete manque (onglet en arriere-plan throttle, veille,
      // hoquet reseau) — deconnecter aussitot ejectait l'utilisateur en pleine session.
      if (opts.auth && !retried) {
        const fresh = await tryRefresh().catch(() => null);
        if (fresh) return apiFetch<T>(path, { ...opts, token: fresh }, true);
        // Le renouvellement a echoue POUR NOUS, mais un autre appel a pu le reussir entre-temps
        // (requetes concurrentes). Si le jeton en stock n'est plus celui qu'on vient d'envoyer,
        // on rejoue avec le nouveau au lieu d'ejecter l'utilisateur (CCT-127).
        const stored = currentToken();
        if (stored && stored !== (opts.token ?? null)) {
          return apiFetch<T>(path, { ...opts, token: stored }, true);
        }
      }
      if (opts.auth) {
        // DIAGNOSTIC (CCT-127) : sans lui, une deconnexion ne laisse aucune trace de l'appel
        // qui l'a declenchee — impossible de distinguer un jeton revoque d'un backend en panne.
        console.warn('[auth] signed out on 401 after a failed refresh:', path);
        // ⚠️ ET SURTOUT : ON L'EMET DANS LA TELEMETRIE.
        //
        // `console.warn` n'est PAS capture par le beacon (seul `console.error` l'est). Le
        // diagnostic ci-dessus n'existait donc que dans une console ouverte au bon moment :
        // dans les logs, une deconnexion se lisait « 401 puis navigate /Login », sans jamais
        // dire si le renouvellement avait ete tente, ni pourquoi il avait echoue. On ne
        // pouvait que constater le symptome, session apres session.
        try {
          // ⚠️ IMPORT DIRECT, PAS D'INJECTION. Ce module importe DEJA le beacon
          // (`causeGarantie`, `debutAppel`) et le beacon n'importe pas ce module : il n'y a
          // aucun cycle a contourner. Un point d'injection aurait ajoute une indirection —
          // et un endroit de plus ou l'on peut oublier de brancher, donc une trace qui
          // disparait sans que personne s'en apercoive.
          track({
            type: 'anomalie', action: 'deconnexion', path: '/auth',
            meta: {
              chemin: path,
              renouvellement: 'echoue',
              jeton_present: !!(opts.token ?? null),
              jeton_change: currentToken() !== (opts.token ?? null),
            },
          });
        } catch {
          /* l'observation ne doit jamais empecher la deconnexion */
        }
        onUnauthorized();
      }
      throw new ApiError(401);
    }

    const text = await res.text();
    let data: any = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    if (!res.ok) throw new ApiError(res.status, data);
    return data as T;
  } finally {
    // Echec reseau ou delai depasse : aucun statut, mais l'appel a EU LIEU et sa latence est
    // justement l'information qu'on cherche. Ne rien emettre le rendrait invisible.
    try { _call?.fin(null); } catch { /* best-effort */ }
    if (loadToken !== undefined) endRequest(loadToken);
  }
}
